import React, { useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { uploadContentImage } from '../services/api';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  // When true, show an image button that uploads and embeds pictures.
  enableImages?: boolean;
  // Storage folder for uploaded images.
  imageFolder?: string;
}

export default function RichTextEditor({ value, onChange, placeholder, className = '', enableImages = false, imageFolder = 'content' }: RichTextEditorProps) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Image.configure({ inline: false, HTMLAttributes: { class: 'rounded-lg max-w-full' } }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[80px] px-4 py-2 text-slate-800',
      },
    },
  });

  const handleImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editor) return;
    setUploading(true);
    try {
      const url = await uploadContentImage(file, imageFolder);
      editor.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      console.error('Image upload failed', err);
      // eslint-disable-next-line no-alert
      alert('Încărcarea imaginii a eșuat.');
    } finally {
      setUploading(false);
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div className={`border border-slate-200 rounded-xl overflow-hidden bg-white ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 border-b border-slate-200 bg-slate-50">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded-lg text-sm font-medium transition-colors ${
            editor.isActive('bold')
              ? 'bg-blue-100 text-blue-700'
              : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Bold"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3 4a1 1 0 011-1h6.5a3.5 3.5 0 012.35 6.1A3.5 3.5 0 0110.5 17H4a1 1 0 01-1-1V4zm4.5 5h2a1.5 1.5 0 000-3h-2v3zm0 2v3h3a1.5 1.5 0 000-3h-3z" />
          </svg>
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded-lg text-sm font-medium transition-colors ${
            editor.isActive('italic')
              ? 'bg-blue-100 text-blue-700'
              : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Italic"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8 3a1 1 0 011-1h6a1 1 0 110 2h-2.36l-2.4 12H12a1 1 0 110 2H6a1 1 0 110-2h2.36l2.4-12H9a1 1 0 01-1-1z" />
          </svg>
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded-lg text-sm font-medium transition-colors ${
            editor.isActive('underline')
              ? 'bg-blue-100 text-blue-700'
              : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Underline"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 3a1 1 0 011 1v7a4 4 0 108 0V4a1 1 0 112 0v7a6 6 0 01-12 0V4a1 1 0 011-1z" />
            <path d="M4 17a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1z" />
          </svg>
        </button>

        <div className="w-px h-6 bg-slate-300 mx-1 self-center" />

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded-lg text-sm font-medium transition-colors ${
            editor.isActive('bulletList')
              ? 'bg-blue-100 text-blue-700'
              : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Bullet List"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 5a1 1 0 100-2 1 1 0 000 2zM7 4a1 1 0 011-1h9a1 1 0 110 2H8a1 1 0 01-1-1zM4 11a1 1 0 100-2 1 1 0 000 2zM7 10a1 1 0 011-1h9a1 1 0 110 2H8a1 1 0 01-1-1zM4 17a1 1 0 100-2 1 1 0 000 2zM7 16a1 1 0 011-1h9a1 1 0 110 2H8a1 1 0 01-1-1z" />
          </svg>
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded-lg text-sm font-medium transition-colors ${
            editor.isActive('orderedList')
              ? 'bg-blue-100 text-blue-700'
              : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Numbered List"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3 3a1 1 0 011-1h1v2H4V3h-.5a.5.5 0 01-.5-.5V2h1V1H3v2zm0 5a1 1 0 011-1h.5v1H4v1h1v1H3V9h1V8H3V7zm0 6v1h2v-1H4v-1h1v-1H3v3zM7 4a1 1 0 011-1h9a1 1 0 110 2H8a1 1 0 01-1-1zM7 10a1 1 0 011-1h9a1 1 0 110 2H8a1 1 0 01-1-1zM7 16a1 1 0 011-1h9a1 1 0 110 2H8a1 1 0 01-1-1z" />
          </svg>
        </button>

        <div className="w-px h-6 bg-slate-300 mx-1 self-center" />

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded-lg text-sm font-medium transition-colors ${
            editor.isActive('heading', { level: 2 })
              ? 'bg-blue-100 text-blue-700'
              : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Heading"
        >
          <span className="text-sm font-bold">H</span>
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-2 rounded-lg text-sm font-medium transition-colors ${
            editor.isActive('blockquote')
              ? 'bg-blue-100 text-blue-700'
              : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Quote"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.5 4a.5.5 0 00-.5.5v5a.5.5 0 00.5.5h2.5v3.5a.5.5 0 00.5.5h1a.5.5 0 00.5-.5V10h1a.5.5 0 00.5-.5v-5A.5.5 0 0010 4H4.5zm7 0a.5.5 0 00-.5.5v5a.5.5 0 00.5.5H14v3.5a.5.5 0 00.5.5h1a.5.5 0 00.5-.5V10h1a.5.5 0 00.5-.5v-5a.5.5 0 00-.5-.5h-5.5z" clipRule="evenodd" />
          </svg>
        </button>

        {enableImages && (
          <>
            <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploading}
              className="p-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors disabled:opacity-50"
              title="Inserează imagine"
            >
              {uploading ? (
                <span className="block w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
            </button>
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImagePick} className="hidden" />
          </>
        )}
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />
    </div>
  );
}
